// Where the document screens are.
//
// - Their paths (`DocumentPaths`): the documents' list and what opens under it
//   (/me/documents in Me; a host may mount the same screens under a base of its
//   own). Every path the screens write comes from here, so the screens never
//   name /me/documents themselves.
// - Where one document screen is (`DocumentPlace`): in the Documents list (the
//   default) or in an index's tree. The screen asks it where a document opens,
//   where leaving goes, beside what a new document is made and what to do with
//   one made here (put it in the index).
import type { PageDTO, PageKindDTO } from "@trameria/core/documents-shared";
import { createContext, useContext } from "react";
import { NEW_DOCUMENT_KEY, NEW_INDEX_KEY } from "./indexes/keys";

/** The base of Me's documents. */
export const ME_DOCUMENTS = "/me/documents";

export interface DocumentPaths {
  /** The list (nothing open). */
  list: string;
  /** A document by its key (or id: its route turns an id into the key). */
  document(key: string): string;
  /** Where a row opens: an index opens as its tree. */
  open(page: { kind: PageKindDTO; key: string }): string;
  /** An index's tree. */
  index(key: string): string;
  /** A document opened from an index (the tree beside it). */
  indexDocument(indexKey: string, key: string): string;
  newDocument: string;
  newIndex: string;
}

export function documentPaths(base: string): DocumentPaths {
  const index = (key: string) => `${base}/index/${key}`;
  return {
    list: base,
    document: (key) => `${base}/${key}`,
    open: (page) =>
      page.kind === "index" ? index(page.key) : `${base}/${page.key}`,
    index,
    indexDocument: (indexKey, key) => `${index(indexKey)}/${key}`,
    newDocument: `${base}/${NEW_DOCUMENT_KEY}`,
    newIndex: index(NEW_INDEX_KEY),
  };
}

export const DocumentPathsContext = createContext<DocumentPaths>(
  documentPaths(ME_DOCUMENTS),
);

export const useDocumentPaths = () => useContext(DocumentPathsContext);

export interface DocumentPlace {
  /** Where the document of `key` opens here. */
  pathOf(key: string): string;
  /** Where leaving goes (Escape on a new document, deleting the open one). */
  back: string;
  /**
   * A new document made here is made beside this document (an index's id or
   * key): in the same place as it. Without it, the list's own way of making
   * one is used.
   */
  beside?: string;
  /** After a new document is made here, before it is opened. */
  created?(page: PageDTO): Promise<unknown>;
}

/** The document screen in the Documents list of `paths`. */
export const listPlace = (paths: DocumentPaths): DocumentPlace => ({
  pathOf: paths.document,
  back: paths.list,
});

export const DocumentPlaceContext = createContext<DocumentPlace>(
  listPlace(documentPaths(ME_DOCUMENTS)),
);

export const useDocumentPlace = () => useContext(DocumentPlaceContext);
